/**
 * The pure half of PIN sign-in. `TEN-006`, `decisions/0021`.
 *
 * `TEN-006`: "Staff can log in with a short username + PIN on a shared counter terminal, where the
 * terminal itself is enrolled and trusted." The spec gives no PIN length, no attempt limit and no
 * lockout duration, so none of those is a constant here: every function takes the number it needs,
 * and the deployment supplies it.
 */
/**
 * The roles that may sign in with a PIN. `decisions/0021` decision 3.
 *
 * Owner and manager are absent because `TEN-005` makes a second factor part of their sign-in, and
 * a PIN that skipped it would be the way round. Held as a list of what is allowed rather than of
 * what is refused, so a role added to Appendix E is refused until somebody decides otherwise.
 */
export const PIN_LOGIN_ROLES = [
    'dispenser',
    'cashier',
    'optician',
    'accountant',
    'read_only',
];
export function canSignInWithPin(role) {
    return PIN_LOGIN_ROLES.includes(role);
}
/** Lowercase ASCII letters and digits, with dot, underscore and hyphen inside. 3 to 20 characters. */
const PIN_USERNAME_SHAPE = /^[a-z0-9][a-z0-9._-]{1,18}[a-z0-9]$/;
/**
 * Trim and lowercase a short username, or return null when it cannot be one.
 *
 * Null rather than a throw: this runs on unauthenticated input at sign-in, where a malformed value
 * has to fail the way a wrong one does, and in `setPin`, where the caller turns null into a
 * sentence.
 */
export function normalisePinUsername(raw) {
    const candidate = raw.normalize('NFKC').trim().toLowerCase();
    return PIN_USERNAME_SHAPE.test(candidate) ? candidate : null;
}
/**
 * Digits only, exactly `length` of them.
 *
 * Western digits only: a JavaScript character class spelled `[0-9]` is ASCII whatever the flags,
 * so Arabic-Indic digits, which a till's keypad would never send, are refused rather than folded.
 */
export function isWellFormedPin(pin, length) {
    if (!Number.isInteger(length) || length <= 0)
        return false;
    return pin.length === length && /^[0-9]+$/.test(pin);
}
/**
 * Whether a PIN may be tried at all.
 *
 * A locked credential is not verified, so a correct PIN is refused until the lock lapses. A lock
 * whose time has passed is no lock, and the count it left behind is zero (the service resets it
 * when it sets the lock).
 *
 * ---
 *
 * **Nothing calls this any more, and that is deliberate rather than rot.** `AuthService.pinLogin`
 * used to read the credential, ask this, and then verify, all inside a transaction holding the row
 * so that a burst of parallel guesses could not step over the limit. Holding a row across two
 * scrypt derivations is a pool anybody with a terminal token can exhaust, so the gate and the spend
 * are now one `UPDATE` whose `WHERE` carries the same rule: not locked, and increment in the same
 * breath. The decision moved into SQL because that is the only place it can be taken atomically
 * without holding anything expensive.
 *
 * Kept, with its tests, because the rule is worth stating in one readable place and because the
 * SQL has to agree with it. **Do not reintroduce a call to it from the login path** without
 * reading `decisions/0021`: a read-then-decide-then-write shape is what was removed.
 */
export function pinAttemptVerdict(state) {
    if (state.lockedUntil !== null && state.lockedUntil.getTime() > state.now) {
        return { kind: 'locked', until: state.lockedUntil };
    }
    return { kind: 'may_try', remaining: Math.max(state.maxFailures - state.failedCount, 0) };
}
//# sourceMappingURL=pin.js.map